import React from 'react';
import { createPortal } from 'react-dom';
import type { ComponentConfig, Fields } from '@puckeditor/core';
import { sanitizeUrl } from '@braintree/sanitize-url';
import styled, { css } from 'styled-components';

import { mediaQueries } from 'src/theme/constants/media';

import { AssetPickerField } from '../fields/widgets/asset-picker-field';
import { focalPointField } from '../fields/widgets/focal-point-field';
import { altTextField, resolveAltText } from '../fields/widgets/alt-text-field';
import type { AltTextValue } from '../fields/widgets/alt-text-field';
import { selectField } from '../fields/widgets/select-field';
import { urlField } from '../fields/widgets/url-field';
import {
  BODY_LINE_HEIGHT,
  BODY_TEXT_SIZES,
  FONT_ROLES,
  IMAGE_ASPECT_RATIOS,
  IMAGE_SIZES,
  lookup,
  TEXT_COLOR_ROLES,
} from '../fields/tokens';
import type { ImageAspectRatio, ImageFocalPoint, ImageMobileBehaviour, ImageRadius, ImageSize } from '../fields/tokens';
import { interpolateFromPuck } from '../interpolation';
import { buildImgixDelivery, isImgixHostedSrc } from '../image-src';
import { resolveImageStyle, imgixSizing } from './image-style';

/**
 * Returns false for src values whose scheme browsers could interpret dangerously
 * (javascript:, vbscript:, data: non-image), true for everything else.
 *
 * `sanitizeUrl` cannot be used here because it rewrites `data:image/png;base64,...` (a legitimate
 * base64 image URI) to `about:blank`, breaking the renderer's byte-identical passthrough guarantee
 * for that shape. This narrower check blocks only the actually-dangerous schemes while leaving
 * every legitimate shape untouched: relative paths, protocol-relative URLs, https://, http://,
 * and data:image/* all pass through unchanged.
 *
 * Leading ASCII whitespace is stripped and the result is lowercased before scheme detection,
 * mirroring how browsers normalise URLs before parsing the scheme (WHATWG URL spec §5.1).
 */
/**
 * Exported so a second image-rendering block cannot end up with a second, subtly different copy of
 * this check. Shared with `slideshow.tsx` and `split-banner.tsx`.
 */
export function isSafeSrc(src: string): boolean {
  // Strip leading ASCII whitespace (tab, LF, FF, CR, space) and lowercase — matches the
  // browser's own pre-parse normalisation step.
  const normalized = src.replace(/^[\t\n\f\r ]+/, '').toLowerCase();
  // Block script-execution schemes via regex — using a regex rather than a string constant
  // avoids the no-script-url lint rule, which scans string literals for those scheme prefixes.
  if (/^(javascript|vbscript):/.test(normalized)) return false;
  // Block data: URIs that are not image types (e.g. data:text/html, data:application/javascript).
  // data:image/* (png, gif, jpeg, webp, svg+xml, ...) are legitimate and must pass through.
  if (normalized.startsWith('data:') && !normalized.startsWith('data:image/')) return false;
  return true;
}

export type AltText = AltTextValue;

const OBJECT_FIT_OPTIONS = [
  { label: 'Fill the frame (may crop)', value: 'cover' },
  { label: 'Fit inside (may leave space)', value: 'contain' },
] as const;

const SIZE_OPTIONS: { label: string; value: ImageSize }[] = [
  { label: 'Fit content', value: 'fit' },
  { label: 'Small', value: 'small' },
  { label: 'Medium', value: 'medium' },
  { label: 'Large', value: 'large' },
  { label: 'Full width', value: 'full' },
];

const ASPECT_RATIO_OPTIONS: { label: string; value: ImageAspectRatio }[] = [
  { label: 'Original', value: 'original' },
  { label: 'Square', value: 'square' },
  { label: 'Landscape (4:3)', value: 'landscape' },
  { label: 'Wide (16:9)', value: 'wide' },
  { label: 'Portrait (3:4)', value: 'portrait' },
];

// Labels disclose shape hazards: Circle forces a square aspect ratio (see the isCircle coupling
// in resolveImageStyle); Pill and Circle render identically on square images because both radii
// clamp to a full semicircle on the shorter side.
const RADIUS_OPTIONS: { label: string; value: ImageRadius }[] = [
  { label: 'None', value: 'none' },
  { label: 'Slightly rounded', value: 'subtle' },
  { label: 'Rounded', value: 'rounded' },
  { label: 'Pill (best on wide images; looks the same as Circle on a square image)', value: 'pill' },
  { label: 'Circle (crops to a square; looks the same as Pill on a square image)', value: 'circle' },
];

// The Image block's "On mobile" control, matching FlexLayout's `mobileStack` naming/shape
// convention (see `tokens.ts`'s `IMAGE_MOBILE_BEHAVIOURS` doc comment).
const MOBILE_OPTIONS: { label: string; value: ImageMobileBehaviour }[] = [
  { label: 'Stretch to fill width', value: 'full' },
  { label: 'Keep chosen size', value: 'keep' },
  { label: 'Hide on mobile', value: 'hide' },
];

// Caption alignment: matches link.tsx's `align` control (same value set, same `radio` field type).
// Text now owns alignment inside richtext. `radio` fields need a manual `ai.schema`; `selectField()` only bakes
// schema for `custom` fields.
const CAPTION_ALIGN_OPTIONS: { label: string; value: CaptionAlign }[] = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
];

type CaptionAlign = 'center' | 'left' | 'right';
type ImageAlign = 'center' | 'left' | 'right';

// Validated via lookup() so an unrecognised value falls back to 'left' rather than reaching the
// DOM as an arbitrary CSS custom property value.
const CAPTION_ALIGNS: Record<string, CaptionAlign> = {
  left: 'left',
  center: 'center',
  right: 'right',
};
const IMAGE_ALIGNS: Record<string, ImageAlign> = {
  left: 'left',
  center: 'center',
  right: 'right',
};
const IMAGE_ALIGN_MARGINS: Record<ImageAlign, string> = {
  left: '0 auto 0 0',
  center: '0 auto',
  right: '0 0 0 auto',
};

const LINK_NEW_TAB_OPTIONS = [
  { label: 'No', value: 'no' },
  { label: 'Yes', value: 'yes' },
] as const;

const FieldTooltipIcon = styled.button`
  align-items: center;
  appearance: none;
  background: var(--dcm-cms-field-tooltip-icon-bg, var(--puck-color-surface-muted, #f7f8fa));
  border: 1px solid var(--dcm-cms-field-tooltip-icon-border-color, var(--puck-color-border, #e1e6eb));
  border-radius: var(--dcm-cms-field-tooltip-icon-radius, 50%);
  color: var(--dcm-cms-field-tooltip-icon-color, var(--puck-color-text-secondary, #45505c));
  cursor: help;
  display: inline-flex;
  flex: 0 0 16px;
  font-size: 11px;
  font-weight: 700;
  height: 16px;
  justify-content: center;
  line-height: 1;
  padding: 0;
  width: 16px;

  &:focus-visible {
    outline: 2px solid var(--dcm-cms-field-tooltip-focus-color, var(--puck-color-focus-ring, #0b99e6));
    outline-offset: 2px;
  }
`;

const PortalTooltip = styled.div<{ $placeAbove: boolean }>`
  background: var(--dcm-cms-field-tooltip-bg, var(--puck-color-surface-inverse, #121516));
  border-radius: var(--dcm-cms-field-tooltip-radius, 6px);
  color: var(--dcm-cms-field-tooltip-color, var(--puck-color-text-inverse, #fff));
  font-family: var(
    --dcm-cms-field-tooltip-font-family,
    var(--puck-font-family, Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif)
  );
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
  max-width: calc(100vw - 16px);
  padding: 8px 10px;
  position: fixed;
  transform: ${({ $placeAbove }) => ($placeAbove ? 'translateY(calc(-100% - 6px))' : 'none')};
  width: 220px;
  z-index: 10000;
`;

type FieldTooltipProps = {
  label: string;
  marksGroupHeading: boolean;
  text: string;
};

function FieldTooltip({ label, text, marksGroupHeading }: FieldTooltipProps): JSX.Element {
  const tooltipId = React.useId();
  const iconRef = React.useRef<HTMLButtonElement>(null);
  const hideTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [position, setPosition] = React.useState({ left: 8, top: 8, placeAbove: false });
  const [isOpen, setIsOpen] = React.useState(false);

  const cancelHide = React.useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = null;
  }, []);

  const show = React.useCallback(() => {
    cancelHide();
    const rect = iconRef.current?.getBoundingClientRect();
    if (!rect) return;
    const tooltipWidth = 240;
    const viewportMargin = 8;
    setPosition({
      left: Math.max(viewportMargin, Math.min(rect.left, window.innerWidth - tooltipWidth - viewportMargin)),
      top: rect.bottom + 6,
      placeAbove: rect.bottom + 100 > window.innerHeight,
    });
    setIsOpen(true);
  }, [cancelHide]);

  const scheduleHide = React.useCallback(() => {
    cancelHide();
    hideTimerRef.current = setTimeout(() => setIsOpen(false), 120);
  }, [cancelHide]);

  React.useEffect(
    () => () => {
      cancelHide();
    },
    [cancelHide]
  );

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const dismiss = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', dismiss);
    return () => document.removeEventListener('keydown', dismiss);
  }, [isOpen]);

  return (
    <>
      <FieldTooltipIcon
        ref={iconRef}
        aria-describedby={isOpen ? tooltipId : undefined}
        aria-label={`Help with ${label}`}
        data-tooltip={text}
        data-cms-image-tooltip='true'
        data-cms-object-group-heading={marksGroupHeading ? 'true' : undefined}
        onBlur={scheduleHide}
        onClick={show}
        onFocus={show}
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
        type='button'
      >
        ?
      </FieldTooltipIcon>
      {isOpen &&
        createPortal(
          <PortalTooltip
            id={tooltipId}
            role='tooltip'
            $placeAbove={position.placeAbove}
            onMouseEnter={cancelHide}
            onMouseLeave={scheduleHide}
            style={{ left: position.left, top: position.top }}
          >
            {text}
          </PortalTooltip>,
          document.body
        )}
    </>
  );
}

function fieldTooltip(label: string, text: string, marksGroupHeading = false): React.ReactElement {
  return <FieldTooltip label={label} text={text} marksGroupHeading={marksGroupHeading} />;
}

export type ImageContent = {
  src: string;
  alt: AltText;
  caption: string;
};

export type ImageAppearance = {
  borderRadius: string;
};

export type ImageLayout = {
  width: string;
  imageAlign: ImageAlign;
  mobile: ImageMobileBehaviour;
  aspectRatio: ImageAspectRatio;
  objectFit: string;
  focalPoint: ImageFocalPoint;
  captionAlign: CaptionAlign;
};

export type ImageLinkSettings = {
  linkHref: string;
  linkOpenInNewTab: 'no' | 'yes';
};

export type ImageProps = {
  content: ImageContent;
  appearance: ImageAppearance;
  layout: ImageLayout;
  link: ImageLinkSettings;
};

/** Placeholder shown in the editor before an image URL has been set. */
export function ImagePlaceholder({
  width = '100%',
  aspectRatio = IMAGE_ASPECT_RATIOS.original,
  borderRadius,
  margin = IMAGE_ALIGN_MARGINS.left,
}: {
  width?: string;
  aspectRatio?: string;
  borderRadius?: string;
  margin?: string;
}): JSX.Element {
  // 'auto' (the Original preset) has no intrinsic size to fall back on before an image URL is
  // set, so give the empty state an explicit height in that case; any real ratio can size itself.
  const isAutoRatio = aspectRatio === 'auto';

  return (
    <div
      style={{
        width,
        margin,
        aspectRatio: isAutoRatio ? undefined : aspectRatio,
        height: isAutoRatio ? 200 : undefined,
        background: 'var(--dcm-cms-image-placeholder-bg, #f0f2f4)',
        borderRadius,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--dcm-cms-image-placeholder-color, #646d72)',
        fontSize: 14,
      }}
    >
      No image URL set
    </div>
  );
}

/**
 * Mobile visibility rule for 'hide'. Applied to {@link StyledImage} when there is no caption, or
 * to {@link StyledFigure} when there is one (hides image and caption together with one rule).
 * Set to `display: none` in production only -- left unset in the editor so the block stays
 * visible/selectable; see render's `isEditing` branch.
 *
 * This is mobile *visibility*; mobile *width* ('full'/'keep', `--cms-image-mobile-width`) is a
 * separate concern that only ever applies to {@link StyledImage}.
 */
const mobileHideOnHideRule = css`
  @media ${mediaQueries.largePhone} {
    display: var(--cms-image-mobile-display, block);
  }
`;

const StyledImage = styled.img`
  display: block;
  width: var(--cms-image-width, 100%);
  margin: var(--cms-image-margin, 0 auto 0 0);
  aspect-ratio: var(--cms-image-aspect-ratio, auto);
  object-fit: var(--cms-image-object-fit, cover);
  object-position: var(--cms-image-focal-point, center center);
  border-radius: var(--cms-image-border-radius, 0);

  @media ${mediaQueries.largePhone} {
    /* Falls back to the desktop width when 'keep' -- see spacer.tsx's mobileHeight for the same
       fallback-chain trick, so 'keep' has no second value to keep in sync. Mobile WIDTH is only
       ever set here, never on StyledFigure -- it governs the image's own box, not the block's
       visibility (see mobileHideOnHideRule above for that separate concern). */
    width: var(--cms-image-mobile-width, var(--cms-image-width, 100%));
  }

  ${mobileHideOnHideRule}
`;

/**
 * Outermost wrapper for the rendered block whenever there's a caption to hide alongside the
 * image -- see `mobileHideOnHideRule` above for why hiding THIS element (rather than duplicating
 * the same rule onto {@link StyledImage} and {@link StyledCaption} individually) is what makes
 * 'Hide on mobile' actually drop the whole block, caption included, instead of leaving a dangling
 * caption with no image above it on phones.
 */
const StyledFigure = styled.figure`
  margin: 0;

  ${mobileHideOnHideRule}
`;

/** Caption typography matches paragraph.tsx's theme-token approach: no per-instance override
 * controls are offered for it, so the tokens are the only fallback layer (no `--cms-image-*`
 * override property needed, since there is nothing an author can set to override it). */
const StyledCaption = styled.figcaption`
  margin: var(--dcm-cms-image-caption-margin, 8px 0 0);
  color: var(--dcm-cms-image-caption-color, ${TEXT_COLOR_ROLES.muted});
  font-family: var(--dcm-cms-image-caption-font-family, ${FONT_ROLES.body});
  font-size: var(--dcm-cms-image-caption-font-size, ${BODY_TEXT_SIZES.sm});
  line-height: var(--dcm-cms-image-caption-line-height, ${BODY_LINE_HEIGHT});
  /* Per-instance override, unlike the theme tokens above -- captionAlign is an author choice,
     not a merchant theme setting, so it goes through a --cms-* (not --dcm-*) property. */
  text-align: var(--cms-image-caption-align, left);
`;

type ImageLinkProps = {
  href: string;
  openInNewTab: 'no' | 'yes';
  isEditing: boolean;
  children: React.ReactNode;
  /** Accessible name for the link when the image inside it contributes no accessible name (e.g.
   * decorative image or empty alt). Only applied to the production `<a>`; the editor `<span>`
   * branch is left alone -- Puck's own selection/drag UI conveys identity there. */
  ariaLabel?: string;
};

/**
 * Wraps the image in a real `<a>` in production, following `link.tsx`'s URL conventions
 * (`sanitizeUrl`, `rel="noopener noreferrer"` only when opening in a new tab).
 *
 * In the Puck editor, an `<a>` around a draggable/selectable block swallows Puck's own
 * drag/selection pointer events (cms/AGENTS.md gotcha #3). A non-interactive `<span>` stands in
 * for the link in the editor; the real `<a>` takes over in production.
 *
 * Renders no wrapper at all, in either mode, when there is no link.
 */
function ImageLink({ href, openInNewTab, isEditing, children, ariaLabel }: ImageLinkProps): JSX.Element {
  if (!href) return <>{children}</>;

  if (isEditing) {
    // Editor: plain, non-interactive element so Puck's own drag/selection handling on the image
    // underneath is not swallowed by a nested interactive <a>.
    return <span style={{ display: 'block' }}>{children}</span>;
  }

  const newTab = openInNewTab === 'yes';
  return (
    <a
      href={sanitizeUrl(href)}
      target={newTab ? '_blank' : undefined}
      // Without noopener the destination gets a window.opener handle back into the storefront.
      rel={newTab ? 'noopener noreferrer' : undefined}
      style={{ display: 'block' }}
      aria-label={ariaLabel}
    >
      {children}
    </a>
  );
}

/** Image block: a URL plus link, sizing, cropping, and captioning controls. */
export const imageConfig: ComponentConfig<ImageProps> = {
  label: 'Image',
  ai: {
    instructions:
      'Displays an image from a URL. Use for hero images, illustrations, and media. Always provide alt text for accessibility, or mark the image decorative if it is purely visual.',
  },
  fields: {
    content: {
      type: 'object',
      label: 'Content',
      labelIcon: fieldTooltip('Content', 'Choose the image itself and its optional visible caption.', true),
      objectFields: {
        src: {
          type: 'custom',
          label: 'Image',
          render: AssetPickerField,
          ai: {
            schema: { type: 'string' },
            stream: false,
            instructions: 'Full image URL. Do not stream partial URLs.',
          },
        },
        alt: altTextField(
          'Alt text',
          'Descriptive alt text for screen readers. Mark the image decorative instead if it conveys no information of its own (e.g. a background flourish).'
        ),
        caption: {
          type: 'text',
          label: 'Caption',
          placeholder: 'Add a caption displayed below the image',
          labelIcon: fieldTooltip('Caption', 'Optional text displayed directly beneath the image.'),
        },
      },
    },
    appearance: {
      type: 'object',
      label: 'Appearance',
      labelIcon: fieldTooltip('Appearance', 'Change the visual treatment without changing the image content.', true),
      objectFields: {
        borderRadius: {
          ...selectField(
            'Corners',
            RADIUS_OPTIONS,
            "Corner treatment. 'Pill' and 'Circle' are shape presets relative to the image's own box, not a themed radius -- 'Circle' also forces a square aspect ratio so it renders as an actual circle. On a square image, 'Pill' and 'Circle' render identically. 'Slightly rounded' and 'Rounded' use this block's own image-scaled radius tokens.",
            "On a square image, 'Pill' and 'Circle' look identical -- both round every corner into a full half-circle."
          ),
          labelIcon: fieldTooltip('Corners', 'Choose how rounded or shaped the image corners appear.'),
        },
      },
    },
    layout: {
      type: 'object',
      label: 'Layout',
      labelIcon: fieldTooltip(
        'Layout',
        'Control image dimensions, cropping, alignment, and responsive behavior.',
        true
      ),
      objectFields: {
        width: selectField(
          'Size',
          SIZE_OPTIONS,
          "Preset image width. 'Fit content' shrinks to the image's own size; 'Full width' fills the column. 'Small'/'Medium'/'Large' use the Image block's own size scale."
        ),
        imageAlign: {
          type: 'radio',
          label: 'Image alignment',
          labelIcon: fieldTooltip('Image alignment', 'Position a non-full-width image within its available space.'),
          options: [...CAPTION_ALIGN_OPTIONS],
          ai: { schema: { type: 'string', enum: ['left', 'center', 'right'] } },
        },
        mobile: selectField(
          'On mobile',
          MOBILE_OPTIONS,
          "'Stretch to fill width' makes the image fill its column on phones regardless of the chosen Size -- almost always correct, since a fixed desktop Size can otherwise leave awkward whitespace or force a squeeze on a narrow screen. 'Keep chosen size' preserves the desktop Size at every breakpoint. 'Hide on mobile' removes the whole block -- image and caption -- on phones (e.g. a desktop-only banner); it stays visible, de-emphasised, in the editor so it's still selectable.",
          "'Hide on mobile' removes the whole block -- image and caption -- on phones. If a Link URL is set, its destination becomes unreachable there too."
        ),
        aspectRatio: selectField(
          'Aspect ratio',
          ASPECT_RATIO_OPTIONS,
          "'Original' uses the image's own proportions, uncropped. The other presets impose a fixed shape, cropping according to the Crop and Focal point controls below. Overridden to a square by Corners' 'Circle' option, regardless of what's chosen here.",
          "Overridden to a square if Corners is set to 'Circle', regardless of what you pick here."
        ),
        objectFit: selectField(
          'Crop',
          OBJECT_FIT_OPTIONS,
          "Only meaningful when Aspect ratio is not 'Original'. 'Fill the frame' crops the image to cover the shape completely; 'Fit inside' shrinks the image so all of it stays visible, which can leave empty space."
        ),
        focalPoint: focalPointField(
          'Focal point',
          'Which part of the image to keep visible when cropping. Only meaningful when Crop is set to fill the frame.',
          "Only has an effect when Crop is set to 'Fill the frame (may crop)'.",
          fieldTooltip('Focal point', 'Choose which part of the image stays visible when cropping.')
        ),
        captionAlign: {
          type: 'radio',
          label: 'Caption alignment',
          labelIcon: fieldTooltip('Caption alignment', 'Align the caption independently of the image itself.'),
          options: [...CAPTION_ALIGN_OPTIONS],
          ai: { schema: { type: 'string', enum: ['left', 'center', 'right'] } },
        },
      },
    },
    link: {
      type: 'object',
      label: 'Link',
      labelIcon: fieldTooltip('Link', 'Optionally make the entire image navigate to another page.', true),
      objectFields: {
        linkHref: urlField('Link URL', {
          placeholder: 'https://example.com/page',
          hint: 'Leave empty for no link.',
          instructions: 'Full URL or relative path to make the whole image a link. Leave empty for no link.',
        }),
        linkOpenInNewTab: {
          type: 'radio',
          label: 'Open in new tab',
          labelIcon: fieldTooltip(
            'Open in new tab',
            'Keep the current page open when a shopper follows the image link.'
          ),
          options: [...LINK_NEW_TAB_OPTIONS],
          ai: { schema: { type: 'string', enum: ['no', 'yes'] } },
        },
      },
    },
  },
  resolveFields: (data, { fields }) => {
    const rawLayout = data.props.layout as unknown;
    const width =
      typeof rawLayout === 'object' && rawLayout !== null && !Array.isArray(rawLayout)
        ? (rawLayout as { width?: unknown }).width
        : undefined;
    const objectFit =
      typeof rawLayout === 'object' && rawLayout !== null && !Array.isArray(rawLayout)
        ? (rawLayout as { objectFit?: unknown }).objectFit
        : undefined;
    const aspectRatio =
      typeof rawLayout === 'object' && rawLayout !== null && !Array.isArray(rawLayout)
        ? (rawLayout as { aspectRatio?: unknown }).aspectRatio
        : undefined;
    const rawContent = data.props.content as unknown;
    const caption =
      typeof rawContent === 'object' && rawContent !== null && !Array.isArray(rawContent)
        ? (rawContent as { caption?: unknown }).caption
        : undefined;
    const rawAppearance = data.props.appearance as unknown;
    const borderRadius =
      typeof rawAppearance === 'object' && rawAppearance !== null && !Array.isArray(rawAppearance)
        ? (rawAppearance as { borderRadius?: unknown }).borderRadius
        : undefined;
    const hasCroppingFrame =
      ASPECT_RATIO_OPTIONS.some(({ value }) => value !== 'original' && value === aspectRatio) ||
      borderRadius === 'circle';

    const layoutField = fields.layout;
    if (layoutField.type !== 'object') return fields;
    const { imageAlign, focalPoint, captionAlign, ...alwaysVisibleLayoutFields } = layoutField.objectFields;
    const { width: widthField, ...remainingLayoutFields } = alwaysVisibleLayoutFields;
    const visibleLayoutFields = {
      width: widthField,
      ...(typeof width === 'string' && width !== '' && width !== 'full' && width !== IMAGE_SIZES.full
        ? { imageAlign }
        : {}),
      ...remainingLayoutFields,
      ...((objectFit ?? 'cover') === 'cover' && hasCroppingFrame ? { focalPoint } : {}),
      ...(typeof caption === 'string' && caption.trim() !== '' ? { captionAlign } : {}),
    };
    return {
      ...fields,
      layout: { ...layoutField, objectFields: visibleLayoutFields },
    } as Fields<ImageProps>;
  },
  defaultProps: {
    content: { src: '', alt: { alt: '', decorative: false }, caption: '' },
    appearance: { borderRadius: 'none' },
    layout: {
      width: 'full',
      imageAlign: 'left',
      mobile: 'full',
      aspectRatio: 'original',
      objectFit: 'cover',
      focalPoint: 'center',
      captionAlign: 'left',
    },
    link: { linkHref: '', linkOpenInNewTab: 'no' },
  },
  render: (rawProps) => {
    const { puck } = rawProps;

    const asObject = (value: unknown): Record<string, unknown> =>
      typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
    const asString = (value: unknown, fallback = ''): string => {
      if (value === undefined) return fallback;
      return typeof value === 'string' ? value : '';
    };

    // Published JSON is unvalidated and <Render> does not merge defaultProps. Normalise each
    // group independently so an absent or malformed group cannot white-screen the whole page.
    const content = asObject(rawProps.content);
    const appearance = asObject(rawProps.appearance);
    const layout = asObject(rawProps.layout);
    const link = asObject(rawProps.link);

    const src = asString(content.src);
    const resolvedAlt = resolveAltText(content.alt);
    const caption = asString(content.caption);
    const borderRadius = asString(appearance.borderRadius, 'none');
    const width = asString(layout.width, 'full');
    const imageAlign = lookup<ImageAlign>(IMAGE_ALIGNS, asString(layout.imageAlign, 'left'), 'left');
    const aspectRatio = asString(layout.aspectRatio, 'original');
    const objectFit = asString(layout.objectFit, 'cover');
    const focalPoint = asString(layout.focalPoint, 'center');
    const captionAlign = lookup<CaptionAlign>(CAPTION_ALIGNS, asString(layout.captionAlign, 'left'), 'left');
    const mobile = asString(layout.mobile, 'full');
    const linkHref = asString(link.linkHref);
    const linkOpenInNewTab = asString(link.linkOpenInNewTab, 'no') === 'yes' ? 'yes' : 'no';
    // Trim at the single point the alt attribute is produced; whitespace-only alt is effectively empty.
    const altAttribute = resolvedAlt.decorative ? '' : resolvedAlt.alt.trim();

    // Computed unconditionally above the early `!src` return so it can gate that return.
    // `puck` is typed as always-present by @puckeditor/core, but some test harnesses render
    // this component's `render` directly without supplying it -- guard defensively.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const isEditing = Boolean(puck?.isEditing);

    const resolved = resolveImageStyle({ width, aspectRatio, objectFit, focalPoint, borderRadius, mobile });

    if (!src || !isSafeSrc(src)) {
      // Only show the placeholder in the editor; production renders nothing for a missing/unsafe src.
      // An empty fragment satisfies the ReactElement return type without emitting a DOM node.
      if (!isEditing) return <></>;

      return (
        <ImagePlaceholder
          // An empty width means "unset", so fall back to the placeholder's own default.
          width={width === '' ? undefined : resolved.width}
          margin={IMAGE_ALIGN_MARGINS[imageAlign]}
          aspectRatio={resolved.aspectRatio}
          borderRadius={resolved.borderRadius}
        />
      );
    }

    // 'hide' sets display:none at the mobile breakpoint in production only. In the editor the
    // block stays visible/selectable; reduced opacity is applied instead (below). WHERE this var
    // lands -- on the image or the figure -- depends on whether there is a caption to hide with it.
    const hideOnMobileStyle: React.CSSProperties =
      resolved.mobileHide && !isEditing ? ({ '--cms-image-mobile-display': 'none' } as React.CSSProperties) : {};

    const imageStyle = {
      '--cms-image-width': resolved.width,
      '--cms-image-margin': IMAGE_ALIGN_MARGINS[imageAlign],
      '--cms-image-aspect-ratio': resolved.aspectRatio,
      '--cms-image-object-fit': resolved.objectFit,
      '--cms-image-focal-point': resolved.focalPoint,
      '--cms-image-border-radius': resolved.borderRadius,
      // 'keep' leaves --cms-image-mobile-width unset so the stylesheet's fallback chain
      // (var(--cms-image-mobile-width, var(--cms-image-width, 100%))) resolves to the desktop
      // width -- the mobile-width fallback-chain trick, same as spacer.tsx's mobileHeight 'same'.
      ...(resolved.mobileWidth ? { '--cms-image-mobile-width': resolved.mobileWidth } : {}),
      // caption.trim() matches the figure branch below: a whitespace-only caption is "no caption"
      // in both places so the hide-var placement can never disagree between the two branches.
      ...(caption.trim() ? {} : hideOnMobileStyle),
    } as React.CSSProperties;

    // Imgix-hosted sources are delivered with optimised src/srcSet/sizes. Non-Imgix sources
    // (relative paths, data: URIs, third-party URLs, raw S3) pass through untouched.
    const imgixDelivery = isImgixHostedSrc(src) ? buildImgixDelivery(src, imgixSizing(width)) : undefined;

    const imgAttributes: React.ImgHTMLAttributes<HTMLImageElement> = {
      src: imgixDelivery?.src ?? src,
      ...(imgixDelivery
        ? { srcSet: imgixDelivery.srcSet, ...(imgixDelivery.sizes !== undefined ? { sizes: imgixDelivery.sizes } : {}) }
        : {}),
      alt: altAttribute,
      loading: 'lazy',
      style: {
        ...imageStyle,
        // Editor-only de-emphasis for 'hide' -- see the CSS var comment above for why production
        // uses `display: none` at the breakpoint instead of this.
        ...(resolved.mobileHide && isEditing ? { opacity: 0.4 } : {}),
      },
    };

    const image = <StyledImage {...imgAttributes} />;

    // WCAG 2.4.4 / 4.1.2: a link must have an accessible name. When the image contributes none
    // (decorative or empty/whitespace alt), use the trimmed caption as the fallback. The URL is
    // deliberately never used -- a raw href is not an accessible name. When altAttribute is
    // non-empty the image already names the link naturally; the isEditing branch emits a <span>
    // so no aria-label is needed there.
    function getLinkAriaLabel(): string | undefined {
      if (!linkHref || altAttribute !== '') return undefined;
      const trimmedCaption = caption.trim();
      return trimmedCaption !== '' ? trimmedCaption : undefined;
    }
    const linkAriaLabel = getLinkAriaLabel();

    // Editor-only warning when a linked image has no accessible name (no alt and no caption).
    // Only fires when decorative/empty-alt meets a live link; a decorative image with no link is
    // a valid authoring choice and must not warn.
    const showLinkedEmptyAltWarning = isEditing && Boolean(linkHref) && altAttribute === '' && !caption.trim();

    const linkedImage = (
      <ImageLink href={linkHref} openInNewTab={linkOpenInNewTab} isEditing={isEditing} ariaLabel={linkAriaLabel}>
        {image}
      </ImageLink>
    );

    // A whitespace-only caption produces no aria-label (getLinkAriaLabel trims above) so it
    // must also produce no figcaption; trim before deciding.
    if (!caption.trim()) {
      if (!showLinkedEmptyAltWarning) return linkedImage;
      // Editor-only fragment: the warning renders below the linked image without affecting
      // production layout. `showLinkedEmptyAltWarning` is always false in production.
      return (
        <>
          {linkedImage}
          <p data-testid='cms-image-linked-empty-alt-warning' style={{ marginTop: 6, fontSize: 12, color: '#a15c00' }}>
            This image links somewhere but has no alt text. Screen reader users will hear an unnamed link. Add alt text
            describing the link destination, or add a caption (it becomes the link&apos;s accessible name).
          </p>
        </>
      );
    }

    // A decorative image is invisible to the accessibility tree, so <figure>/<figcaption>
    // would caption nothing the user can perceive -- use <div>/<p> via styled-components' `as`.
    const wrapperTag = resolvedAlt.decorative ? 'div' : 'figure';
    const captionTag = resolvedAlt.decorative ? 'p' : 'figcaption';

    // Caption supports {{token}} interpolation like Heading/Text.
    const resolvedCaption = interpolateFromPuck(caption, puck);

    return (
      // The hide-on-mobile var goes on the figure so it hides image and caption together.
      <StyledFigure as={wrapperTag} style={hideOnMobileStyle}>
        {linkedImage}
        <StyledCaption as={captionTag} style={{ '--cms-image-caption-align': captionAlign } as React.CSSProperties}>
          {resolvedCaption}
        </StyledCaption>
      </StyledFigure>
    );
  },
};
